'use client';

import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { type ElementType, type ReactNode } from 'react';

import { focusRingStyles } from '../../../styles/focusRing';
import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../../types/component';
import { mergeProps } from '../../../utils/mergeProps';
import {
  type InteractionColorConfig,
  interactionStyles,
} from '../../styles/interaction';
import { disableInteractions } from '../../utils/disableInteractions';

// ─── Config types ───

type InteractableColorConfig = InteractionColorConfig & {
  focusColor?: 'default' | 'static-white';
};

type InteractionState = {
  isFocused: boolean;
  isFocusVisible: boolean;
  isHovered: boolean;
  isPressed: boolean;
};

interface InteractableBaseProps {
  /** Children, or a render function receiving interaction state. */
  children?: ReactNode | ((state: InteractionState) => ReactNode);

  /** Disable hover tracking. @default false */
  disableHover?: boolean;

  /** Disable focus tracking. @default false */
  disableFocus?: boolean;

  /** Focus within mode — detect child element focus. @default false */
  focusWithin?: boolean;

  /**
   * Interaction styling configuration.
   * - `backgroundColor`, `borderColor`, `textColor`: colours for hover + pressed states.
   * - `bgMode`: which set of background tokens to use (`'filled'` | `'blank'` | `'elevated'`).
   * - `focusColor`: focus ring colour preset (`'default'` | `'static-white'`).
   * Disabled background and border styles are derived automatically from colour props.
   */
  interactionColors?: InteractableColorConfig;

  /** Whether the element is disabled. */
  isDisabled?: boolean;

  /** Whether the element is pressed. */
  isPressed?: boolean;
}

const DEFAULT_ELEMENT_TYPE = 'div';
type DefaultElementType = typeof DEFAULT_ELEMENT_TYPE;

type InteractableProps<RootComponent extends ElementType = DefaultElementType> =
  PolymorphicComponentProps<InteractableBaseProps, RootComponent>;

type InteractableComponent = PolymorphicComponent<
  InteractableBaseProps,
  DefaultElementType
>;

const Interactable: InteractableComponent = (props) => {
  const {
    component: Root = DEFAULT_ELEMENT_TYPE,
    disableHover = false,
    disableFocus = false,
    focusWithin = false,
    autoFocus,
    interactionColors,
    isDisabled = false,
    isPressed = false,
    children,
    className,
    ref,
    ...rest
  } = props as InteractableProps;

  const { hoverProps, isHovered } = useHover({
    isDisabled: isDisabled || disableHover,
  });

  const { focusProps, isFocused, isFocusVisible } = useFocusRing({
    autoFocus,
    within: focusWithin,
  });

  const { focusColor, ...colorTokens } = interactionColors ?? {};

  const focusClass = disableFocus
    ? null
    : focusRingStyles({
        color: focusColor === 'static-white' ? 'static-white' : 'default',
      });

  const rootProps = disableInteractions(
    mergeProps(
      rest,
      disableHover ? {} : hoverProps,
      disableFocus ? {} : focusProps
    ),
    { isDisabled }
  );

  const mergedClassName = interactionStyles({
    ...colorTokens,
    class: focusClass ? focusClass.root({ class: className }) : className,
  });

  const state: InteractionState = {
    isFocused: !isDisabled && isFocused,
    isFocusVisible: !isDisabled && isFocusVisible,
    isHovered: !isDisabled && isHovered,
    isPressed: !isDisabled && isPressed,
  };

  return (
    <Root
      {...rootProps}
      className={mergedClassName || undefined}
      data-hovered={state.isHovered || undefined}
      data-pressed={state.isPressed || undefined}
      data-focused={state.isFocused || undefined}
      data-focus-visible={state.isFocusVisible || undefined}
      data-disabled={isDisabled || undefined}
      autoFocus={autoFocus}
      ref={ref}
    >
      {typeof children === 'function' ? children(state) : children}
    </Root>
  );
};

Interactable.displayName = 'Interactable';

export { Interactable, type InteractableProps, type InteractableBaseProps };
export type { InteractionColorConfig, InteractionState };
